import { useCheckout } from 'components/Checkout/CheckoutContext'
import { getCartBookStores } from 'components/Checkout/unsupportedProductValidation'
import { analytics, EVENT_NAMES } from 'services/Analytics/Analytics'
import { getGiftCardTypeForAnalytics } from 'services/Analytics/giftCardAnalytics'
import {
  getProductTypeForEvent,
  trackCheckoutSheetOpened,
} from 'services/Analytics/orderAnalytics'
import { ServerSideAnalyticsPlatform } from 'services/Analytics/ServerSideAnalytics'
import { getUploadStatusProps } from 'services/Analytics/uploadAnalytics'
import { type PaymentMethodType } from 'services/PaymentManager.types'
import { getBookPhotosUids } from 'services/PhotoBook/photoBookUtils'
import { useProductType } from 'services/ProductTypesManager'
import { useCartStore } from 'stores/cartStore/CartStoreProvider'
import {
  usePhotoBankStore,
  usePhotoBooksStore,
  useTilesStore,
} from 'stores/RootStoreProvider'
import { readOutsideReaction } from 'utils/mobx/readOutsideReaction'
import { type CheckoutEntryPoint } from '../cart.consts'
import { useBagAnalytics } from './useBagAnalytics'

export function useCheckoutFunnelAnalytics({
  isCartFlow,
}: {
  isCartFlow: boolean
}) {
  const { productType } = useProductType()
  const checkout = useCheckout()
  const cart = useCartStore()
  const tilesStore = useTilesStore()
  const photoBankStore = usePhotoBankStore()
  const photoBooksStore = usePhotoBooksStore()
  const { getBagProps } = useBagAnalytics()

  const trackCheckoutStarted = (entryPoint: CheckoutEntryPoint) => {
    trackCheckoutSheetOpened({
      orderTiles: checkout.orderTiles,
      orderItems: checkout.orderItems,
      orderPriceSummary: checkout.pricingSummary,
      includePricingSummaryFields: !checkout.error,
      productType,
      bagProps: isCartFlow ? getBagProps() : {},
      entryPoint,
    })
  }

  const getBagPhotoUids = () => [
    ...getCartBookStores(cart, photoBooksStore).flatMap((bookStore) =>
      bookStore.bookData
        ? getBookPhotosUids(bookStore.bookData.cover, bookStore.bookData.pages)
        : []
    ),
    ...tilesStore.getTilePhotosUids(),
  ]

  const getBagUploadStatusProps = () =>
    readOutsideReaction(() =>
      getUploadStatusProps(photoBankStore, getBagPhotoUids())
    )

  const getGiftCardTypeProps = () => {
    const giftCardType = checkout.orderItems[0]?.metadata?.giftCardType
    return giftCardType
      ? { 'Gift Card Type': getGiftCardTypeForAnalytics(giftCardType) }
      : {}
  }

  const trackCompleteOrderTapped = (paymentMethodType?: PaymentMethodType) => {
    analytics.track(
      EVENT_NAMES.COMPLETE_ORDER_TAPPED,
      {
        'Product Type': getProductTypeForEvent(
          productType,
          checkout.orderItems[0]
        ),
        'Payment Method': paymentMethodType,
        ...(isCartFlow ? getBagUploadStatusProps() : getGiftCardTypeProps()),
      },
      { serverSideAnalyticsPlatforms: [ServerSideAnalyticsPlatform.Tiktok] }
    )
  }

  return { trackCheckoutStarted, trackCompleteOrderTapped }
}
